import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TbChevronDown,
  TbChevronUp,
  TbGripVertical,
  TbPencil,
} from 'react-icons/tb';

import { Fields, Sources } from '../generator/types';

import { BlockIcon } from './BlockIcon';
import {
  resolveParagraphContact,
  resolveParagraphs,
  resolveParagraphText,
} from './blockResolver';
import { LetterBlock, ParagraphTemplate } from './blocks';
import {
  DATE_INPUT_SELECTOR,
  DATE_WRAP_SELECTOR,
  normalizeSavedParagraphText,
  syncDateDisplay,
} from './dateInput';
import { colors } from './drawerStyles';
import { blockValueKey, readFieldValue } from './fieldKeys';
import { buildParagraphHtml } from './HtmlMarkerRenderer';
import {
  applyInAdditionPrefix,
  collectFieldPlaceholders,
  IN_ADDITION_SENTINEL,
} from './letterBodyBuilder';

export interface BlockCardProps {
  block: LetterBlock;
  index: number;
  isJuvenile: boolean;
  fieldValues: Record<string, string>;
  riders: string[];
  carrierDisplayName: string;
  onFieldChange: (key: string, value: string) => void;
  onRemove: () => void;
  onDragStart: (id: Sources) => void;
  onDragOver: (id: Sources) => void;
  onDragEnd: () => void;
  isDraggingOver: boolean;
  isDragSrc: boolean;
}

export const BlockCard = (props: BlockCardProps) => {
  const placeholders = useMemo(
    () => collectFieldPlaceholders([props.block]),
    [props.block],
  );
  const paragraphs = resolveParagraphs(props.block, props.isJuvenile);
  const isCustom = props.block.id === Sources.custom;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;
    const onExpand = () => setIsCollapsed(false);
    el.addEventListener('nora-block-expand', onExpand);
    return () => el.removeEventListener('nora-block-expand', onExpand);
  }, []);

  const isDraggable = !isCustom;

  return (
    <div
      ref={cardRef}
      id={`block-${props.block.id}`}
      onDragOver={e => {
        e.preventDefault();
        if (isDraggable) props.onDragOver(props.block.id);
      }}
      onDrop={e => {
        e.preventDefault();
        props.onDragEnd();
      }}
      style={{
        background: colors.surface,
        border: `1px solid ${props.isDraggingOver ? colors.blue : colors.border}`,
        borderRadius: 7,
        overflow: 'hidden',
        boxShadow: props.isDraggingOver
          ? `0 0 0 2px ${colors.blueBg}`
          : undefined,
        opacity: props.isDragSrc ? 0.45 : 1,
        transition: 'opacity 0.1s, box-shadow 0.1s',
        flexShrink: 0,
      }}
    >
      {/* Header — draggable, click to collapse */}
      <div
        draggable={isDraggable}
        onDragStart={() => isDraggable && props.onDragStart(props.block.id)}
        onDragEnd={props.onDragEnd}
        onClick={() => setIsCollapsed(c => !c)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: colors.surface2,
          borderBottom: isCollapsed ? 'none' : `1px solid ${colors.border}`,
          cursor: isDraggable
            ? props.isDragSrc
              ? 'grabbing'
              : 'grab'
            : 'pointer',
        }}
      >
        {isDraggable ? (
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 18,
              color: colors.text3,
              flexShrink: 0,
              pointerEvents: 'none',
            }}
          >
            <TbGripVertical size={14} />
          </span>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} aria-hidden />
        )}
        <span
          style={{
            fontSize: 9,
            fontFamily: 'monospace',
            fontWeight: 500,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            padding: '1px 4px',
            color: colors.text2,
          }}
        >
          {props.index + 1}
        </span>
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 3,
            background: colors.blueBg,
            border: `1px solid ${colors.blueBorder}`,
            color: colors.blueText,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BlockIcon name={props.block.icon} size={11} />
        </span>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 500,
            color: colors.text2,
            flex: 1,
          }}
        >
          {props.block.label}
          {props.index > 0 && !isCustom ? (
            <span
              style={{
                fontSize: 9,
                fontFamily: 'monospace',
                background: colors.purpleBg,
                border: `1px solid ${colors.purpleBorder}`,
                color: colors.purpleText,
                borderRadius: 3,
                padding: '1px 4px',
                marginLeft: 6,
              }}
            >
              In addition…
            </span>
          ) : null}
        </span>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            setIsCollapsed(c => !c);
          }}
          aria-label={
            isCollapsed
              ? `Expand ${props.block.label}`
              : `Collapse ${props.block.label}`
          }
          style={{
            width: 18,
            height: 18,
            borderRadius: 3,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: colors.text3,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
          }}
        >
          {isCollapsed ? (
            <TbChevronDown size={12} />
          ) : (
            <TbChevronUp size={12} />
          )}
        </button>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            props.onRemove();
          }}
          aria-label={`Remove ${props.block.label}`}
          style={{
            width: 18,
            height: 18,
            borderRadius: 3,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: colors.text3,
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      {!isCollapsed ? (
        <>
          <div
            style={{
              padding: '9px 11px',
              fontSize: 12,
              lineHeight: 1.65,
              color: colors.text,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {isCustom ? (
              <CustomBlockBody
                value={
                  readFieldValue(
                    props.fieldValues,
                    Fields.customContent,
                    props.block.id,
                  ) ?? ''
                }
                onChange={v =>
                  props.onFieldChange(
                    blockValueKey(props.block.id, Fields.customContent),
                    v,
                  )
                }
              />
            ) : (
              paragraphs.map((p: ParagraphTemplate, paraIdx) => {
                const rawText = resolveParagraphText(p, props.isJuvenile);
                const prefixed = applyInAdditionPrefix(
                  rawText,
                  props.index,
                  paraIdx,
                  { mode: 'sentinel' },
                );
                const hasInAddition = prefixed.startsWith(IN_ADDITION_SENTINEL);
                const cleanedText = hasInAddition
                  ? prefixed.slice(IN_ADDITION_SENTINEL.length)
                  : prefixed;
                const contact = resolveParagraphContact(p, props.isJuvenile);
                const paragraphFieldKeys = p.fields
                  .map(f => f.key as string)
                  .filter(k => k !== 'riders' && k !== 'carrierDisplayName');
                const paraKey = `__para_${props.block.id}_${paraIdx}`;
                const paraHtmlKey = `${paraKey}_html`;
                const contactKey = `__contact_${props.block.id}_${paraIdx}`;
                const contactHtmlKey = `${contactKey}_html`;
                return (
                  <div
                    key={paraIdx}
                    style={{
                      marginBottom: paraIdx < paragraphs.length - 1 ? 7 : 0,
                    }}
                  >
                    <ParagraphEditor
                      templateHtml={
                        (hasInAddition
                          ? '<span style="color:#6D28D9;font-weight:500;">In addition, </span>'
                          : '') +
                        buildParagraphHtml(
                          cleanedText,
                          props.fieldValues,
                          placeholders,
                          props.riders,
                          props.carrierDisplayName,
                          props.block.id,
                        )
                      }
                      fieldValues={props.fieldValues}
                      paragraphFieldKeys={paragraphFieldKeys}
                      paraKey={paraKey}
                      paraHtmlKey={paraHtmlKey}
                      blockId={props.block.id}
                      onFieldChange={props.onFieldChange}
                      onRemove={props.onRemove}
                    />
                    {contact ? (
                      <div
                        style={{
                          marginTop: 7,
                          padding: '6px 8px',
                          background: colors.surface2,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 5,
                          fontFamily: 'monospace',
                          fontSize: 11,
                          color: colors.text2,
                          lineHeight: 1.7,
                          whiteSpace: 'pre',
                        }}
                      >
                        <ParagraphEditor
                          templateHtml={buildParagraphHtml(
                            contact,
                            props.fieldValues,
                            placeholders,
                            props.riders,
                            props.carrierDisplayName,
                            props.block.id,
                          )}
                          fieldValues={props.fieldValues}
                          paragraphFieldKeys={[]}
                          paraKey={contactKey}
                          paraHtmlKey={contactHtmlKey}
                          onFieldChange={props.onFieldChange}
                          blockId={props.block.id}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
          {!isCustom ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 11px 4px',
                fontSize: 10,
                color: colors.text3,
                background: colors.surface2,
                borderTop: `1px solid ${colors.border}`,
                fontFamily: 'monospace',
              }}
            >
              <TbPencil size={10} aria-hidden />
              Click any text to edit
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

interface ParagraphEditorProps {
  templateHtml: string;
  fieldValues: Record<string, string>;
  paragraphFieldKeys: string[];
  blockId: string;
  paraKey: string;
  paraHtmlKey: string;
  onFieldChange: (key: string, value: string) => void;
  onRemove?: () => void;
}

const ParagraphEditor = ({
  templateHtml,
  fieldValues,
  paragraphFieldKeys,
  blockId,
  paraKey,
  paraHtmlKey,
  onFieldChange,
  onRemove,
}: ParagraphEditorProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  // Tracks whether the next effect run is the initial mount.
  const isFirstRender = useRef(true);

  const attachDateInputs = () => {
    if (!ref.current) return;
    ref.current
      .querySelectorAll<HTMLInputElement>(DATE_INPUT_SELECTOR)
      .forEach(input => {
        // Saved innerHTML carries the input's original value and label, not what
        // was picked since, so re-sync both from the stored ISO value.
        const storedKey = input.getAttribute('data-field-input');
        const stored = storedKey
          ? readFieldValue(fieldValues, storedKey, blockId)
          : undefined;
        if (stored !== undefined) input.value = stored;
        syncDateDisplay(input);
        if ((input as HTMLInputElement & { _noraBound?: boolean })._noraBound)
          return;
        (input as HTMLInputElement & { _noraBound?: boolean })._noraBound =
          true;
        input.addEventListener('input', () => {
          syncDateDisplay(input);
          const key = input.getAttribute('data-field-input');
          // A native date input always reports ISO, or '' while incomplete.
          if (key) onFieldChange(blockValueKey(blockId, key), input.value);
        });
        // The input is invisible over the label, so open the calendar on any
        // click (browsers otherwise only open it from the calendar icon).
        input.addEventListener('click', () => {
          try {
            input.showPicker?.();
          } catch {
            // Not allowed here (e.g. no user gesture); the input still works.
          }
        });
        input.addEventListener('mousedown', e => e.stopPropagation());
      });
  };

  // Single effect handling both mount and subsequent updates:
  //
  // • On mount: restore from saved HTML (paraHtmlKey) so freeform edits survive
  //   minimize/expand and individual card collapse (RP-7984). Falls back to
  //   templateHtml when no override exists.
  //
  // • On subsequent renders while unfocused: if the user has previously made
  //   freeform edits (paraHtmlKey is set), patch only the [data-field] spans
  //   in-place rather than resetting the full innerHTML — this preserves the
  //   freeform text between placeholders (RP-7975, RP-7987). Falls back to the
  //   original full reset when no freeform override is stored.
  useEffect(() => {
    if (!ref.current) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      const savedHtml = fieldValues[paraHtmlKey];
      ref.current.innerHTML =
        savedHtml !== undefined ? savedHtml : templateHtml;
      attachDateInputs();
      return;
    }

    if (document.activeElement === ref.current) return;
    if (ref.current.contains(document.activeElement)) return;

    if (fieldValues[paraHtmlKey] !== undefined) {
      // Patch [data-field] spans in-place so freeform text edits are preserved.
      ref.current.querySelectorAll<HTMLElement>('[data-field]').forEach(span => {
        const key = span.getAttribute('data-field');
        if (!key) return;
        const value = readFieldValue(fieldValues, key, blockId);
        const placeholder = span.getAttribute('data-placeholder') ?? '';
        const current = (span.innerText ?? '').replace(/​/g, '');
        const target =
          value !== undefined && value !== '' ? value : placeholder;
        if (current !== target) {
          span.textContent = target;
        }
      });
      ref.current
        .querySelectorAll<HTMLInputElement>(DATE_INPUT_SELECTOR)
        .forEach(input => {
          const key = input.getAttribute('data-field-input');
          const value = key
            ? readFieldValue(fieldValues, key, blockId)
            : undefined;
          if (value !== undefined) {
            input.value = value;
            syncDateDisplay(input);
          }
        });
      return;
    }

    ref.current.innerHTML = templateHtml;
    attachDateInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues, templateHtml]);

  const extractFields = () => {
    if (!ref.current) return;
    const seen = new Set<string>();
    ref.current.querySelectorAll<HTMLElement>('[data-field]').forEach(span => {
      const key = span.getAttribute('data-field');
      if (!key) return;
      seen.add(key);
      const placeholder = span.getAttribute('data-placeholder') ?? '';
      // Strip zero-width spaces (inserted to keep the caret inside an emptied box).
      const text = (span.innerText ?? '').replace(/​/g, '');
      // If the user hasn't touched this field, the span still shows its
      // placeholder. Don't save the placeholder text as the field value —
      // that would falsely satisfy validation and clear the required-field banner.
      if (text === placeholder) return;
      onFieldChange(blockValueKey(blockId, key), text);
    });
    ref.current
      .querySelectorAll<HTMLInputElement>(DATE_INPUT_SELECTOR)
      .forEach(input => {
        const key = input.getAttribute('data-field-input');
        if (key) seen.add(key);
      });
    // Any template field that's no longer in the DOM was deleted by the user.
    // Clear its stored value AND mark it removed so validation skips it.
    for (const key of paragraphFieldKeys) {
      if (seen.has(key)) continue;
      const valueKey = blockValueKey(blockId, key);
      if (fieldValues[valueKey]) onFieldChange(valueKey, '');
      const removedKey = `__field_removed_${blockId}_${key}`;
      if (fieldValues[removedKey] !== '1') onFieldChange(removedKey, '1');
    }

    // Persist the full paragraph so freeform edits survive Copy, Preview,
    // Upload, minimize/expand, and individual card collapse (RP-7975, RP-7987,
    // RP-7984). paraKey stores resolved plain text for Copy/PDF output;
    // paraHtmlKey stores the rendered HTML for DOM restoration on remount.
    const paraClone = ref.current.cloneNode(true) as HTMLElement;
    paraClone
      .querySelectorAll<HTMLInputElement>(DATE_INPUT_SELECTOR)
      .forEach(input => {
        const key = input.getAttribute('data-field-input') ?? '';
        (input.closest(DATE_WRAP_SELECTOR) ?? input).replaceWith(`{{${key}}}`);
      });
    onFieldChange(
      paraKey,
      normalizeSavedParagraphText(paraClone.textContent ?? ''),
    );
    onFieldChange(paraHtmlKey, ref.current.innerHTML);
  };

  return (
    <div
      ref={ref}
      data-paragraph
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        extractFields();
      }}
      onInput={extractFields}
      onMouseUp={e => {
        const target = (e.target as HTMLElement).closest<HTMLElement>(
          '[data-field]',
        );
        if (!target) return;
        const placeholder = target.getAttribute('data-placeholder') ?? '';
        const text = (target.innerText ?? '').replace(/​/g, '');
        if (text !== placeholder) return;
        const range = document.createRange();
        range.selectNodeContents(target);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }}
      onKeyDown={e => {
        if (e.key !== 'Backspace' && e.key !== 'Delete') return;
        // Keys typed inside the date box belong to the box, not the paragraph.
        if ((e.target as HTMLElement).tagName === 'INPUT') return;

        // If the caret is inside a [data-field] span and this keystroke would
        // clear its content, prevent default so the span itself isn't removed —
        // the box should stay visible. A second backspace on the empty box
        // then lets the browser remove the span normally.
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          let node: Node | null = range.startContainer;
          if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
          const span = (node as HTMLElement | null)?.closest?.(
            '[data-field]',
          ) as HTMLElement | null;
          if (span) {
            const text = span.textContent ?? '';
            if (text.length > 0) {
              const selectsAll = sel.toString() === text && text.length > 0;
              const lastCharBackspace =
                e.key === 'Backspace' &&
                text.length === 1 &&
                range.startOffset === 1 &&
                range.collapsed;
              const lastCharDelete =
                e.key === 'Delete' &&
                text.length === 1 &&
                range.startOffset === 0 &&
                range.collapsed;
              if (selectsAll || lastCharBackspace || lastCharDelete) {
                e.preventDefault();
                // Zero-width space gives the caret a text node to sit inside the
                // box so it doesn't drop to the next line visually.
                span.textContent = '​';
                const newRange = document.createRange();
                if (span.firstChild) {
                  newRange.setStart(span.firstChild, 1);
                  newRange.collapse(true);
                  sel.removeAllRanges();
                  sel.addRange(newRange);
                }
                const key = span.getAttribute('data-field');
                if (key) onFieldChange(blockValueKey(blockId, key), '');
                return;
              }
            }
          }
        }

        if (onRemove && !ref.current?.textContent?.trim()) {
          e.preventDefault();
          onRemove();
        }
      }}
      style={{
        outline: 'none',
        borderRadius: 3,
        padding: '1px 2px',
        margin: '-1px -2px',
        background: isFocused ? '#FFFBEB' : 'transparent',
        ...(isFocused ? { outline: '1px solid #FDE68A' } : {}),
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        cursor: 'text',
      }}
    />
  );
};

interface CustomBlockBodyProps {
  value: string;
  onChange: (value: string) => void;
}

const CustomBlockBody = ({ value, onChange }: CustomBlockBodyProps) => {
  const isEmpty = !value || value.trim() === '';
  return (
    <div>
      <textarea
        maxLength={2500}
        placeholder="Type custom paragraph text here..."
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          minHeight: 60,
          resize: 'vertical',
          fontSize: 13,
          lineHeight: 1.65,
          color: colors.text,
          background: isEmpty ? '#FEF2F2' : colors.surface,
          border: `1px solid ${isEmpty ? '#FCA5A5' : colors.border}`,
          borderRadius: 5,
          padding: '8px 10px',
          outline: 'none',
          fontFamily: 'inherit',
        }}
      />
      <div
        style={{
          textAlign: 'right',
          fontSize: 10,
          fontFamily: 'monospace',
          color: colors.text3,
          marginTop: 3,
        }}
      >
        {value.length} / 2500
      </div>
    </div>
  );
};
